import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QrAPI } from "../../services/api";
import { isQrActive } from "../../utils/qrStatus";

/**
 * เปิดจาก mobile หลังสแกน QR ที่ encode URL: /scan/<code>
 * 1. resolve code → ได้ qr/floor/building
 * 2. log scan
 * 3. redirect ไปหน้า user navigation พร้อม set จุดเริ่มต้น
 */
export default function ScanRedirect() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState("loading"); // loading | error
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        const res = await QrAPI.getByCode(code);
        if (cancelled) return;

        const qr = res?.qr || res; // tolerant ต่อ shape ของ backend
        const floor = res?.floor;
        const building = res?.building;

        if (!qr?.id) throw new Error(t("user.scanRedirect.qrNotFound"));
        if (!isQrActive(qr)) throw new Error("QR Code นี้ถูกปิดใช้งานแล้ว");

        // log scan (fire-and-forget)
        const sessionId = getSessionId();
        QrAPI.scan(qr.id, sessionId, navigator.userAgent).catch(() => {});

        // redirect ไปหน้าแผนที่หลัก พร้อมเลือกอาคาร/ชั้น และปัก "คุณอยู่ที่นี่"
        const params = new URLSearchParams();
        params.set("currentQrId", qr.id);
        const bId = building?.id ?? qr.buildingId;
        const fId = floor?.id ?? qr.floorId;
        if (bId) params.set("buildingId", String(bId));
        if (fId) params.set("floorId", String(fId));

        navigate(`/user/mainpage?${params.toString()}`, { replace: true });
      } catch (e) {
        if (cancelled) return;
        setErrMsg(e.message || t("user.scanRedirect.cannotRead"));
        setStatus("error");
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [code, navigate, t]);

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white rounded-2xl shadow p-6 max-w-sm w-full text-center">
          <div className="text-4xl mb-3">⚠️</div>
          <div className="font-bold text-slate-800 mb-1">{t("user.scanRedirect.scanFailed")}</div>
          <div className="text-sm text-slate-500 mb-4">{errMsg}</div>
          <button
            onClick={() => navigate("/user/mainpage", { replace: true })}
            className="w-full h-10 rounded-lg bg-blue-600 text-white font-bold"
          >
            {t("user.scanRedirect.backToHome")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-slate-400 text-sm">{t("user.scanRedirect.openingMap")}</div>
    </div>
  );
}

function getSessionId() {
  const KEY = "indoor_nav_session_id";
  let sid = localStorage.getItem(KEY);
  if (!sid) {
    sid =
      (crypto?.randomUUID && crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(KEY, sid);
  }
  return sid;
}
